// The Project Datasheet Composer's layout editor: the boxes of one Index Sheet Datasheet revision on
// the same canvas the Master Composer uses. The server decides whether the revision may be edited
// (a Draft copy of the sheet's own datasheet that nothing else uses) and applies the saved changes
// to the template library and this project together. Removing boxes that hold values lists those
// values first and deletes them only on an explicit confirm.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Save } from 'lucide-react';
import { dsProjectApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { sheetLayout } from '../development/dsTemplateLayout';
import { useDsTemplates } from '../development/useDsTemplates';
import { SHARED_SHEET } from '../development/pdf/dsGroupBySheet';
import { isSharedZone, storedZoneFor, onSheet, linesToBoxes, boxToLine, lineKey } from './dsBoxes';
import { DSCanvas } from './DSCanvas';
import { ConfirmModal } from './CatalogModals';
import { errText, layoutChanges } from './projectSheets';

const SHOWN_VALUES = 50;

export default function ProjectLayoutEditor({ row, onClose }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const specRows = useDsTemplates(dsProjectApi.getLayoutSpec);

  // Real box id -> lineKey as last read from the server; the save diff is taken against it.
  const baselineRef = useRef(new Map());
  const savedRef = useRef(false);                  // something was saved, so the list must reload on close
  const [meta, setMeta] = useState(null);              // the server's layout answer
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxesRaw] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSheet, setActiveSheet] = useState(1);
  const [confirm, setConfirm] = useState(null);        // { kind: 'values', values } | { kind: 'discard' }

  const setBoxes = useCallback(updater => { setBoxesRaw(updater); setHasChanges(true); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dsProjectApi.getLayout(row.dsConfig);
      setMeta(data);
      if (data.editable) {
        const loaded = linesToBoxes(data.lines ?? []);
        baselineRef.current = new Map(loaded.map(b => [b.id, lineKey(boxToLine(b, null))]));
        setBoxesRaw(loaded);
      }
      setHasChanges(false);
    } catch (e) {
      setMeta({ editable: false, reason: errText(e, 'Failed to load the layout.') });
    } finally { setLoading(false); }
  }, [row.dsConfig]);

  useEffect(() => { load(); }, [load]);

  // The browser's own prompt when the tab is closed with unsaved boxes.
  useEffect(() => {
    if (!hasChanges) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges]);

  const sheetCount = Math.max(1, meta?.sheetCount ?? 1);
  const sheetNumbers = useMemo(() => Array.from({ length: sheetCount }, (_, i) => i + 1), [sheetCount]);
  // The last page is Application Specific by definition; a one-page template has no such page.
  const sheetLabel = n => (sheetCount > 1 && n === sheetCount ? 'Application Specific' : `Content ${n}`);

  useEffect(() => { if (activeSheet > sheetCount) setActiveSheet(1); }, [sheetCount, activeSheet]);

  const sheetBoxes = useMemo(() => boxes.filter(b => onSheet(b, activeSheet)), [boxes, activeSheet]);
  const layout = useMemo(
    () => sheetLayout(specRows, meta?.tempType ?? null, activeSheet, sheetCount, meta?.dsName ?? null),
    [specRows, meta?.tempType, meta?.dsName, activeSheet, sheetCount]);

  // DSCanvas replaces the whole list it is handed, so it gets only the active sheet and a setter
  // that stitches the other sheets back around the result (same as the Master Composer).
  const setSheetBoxes = useCallback(updater => {
    setBoxes(prev => {
      const mine = prev.filter(b => onSheet(b, activeSheet));
      const others = prev.filter(b => !onSheet(b, activeSheet));
      const next = typeof updater === 'function' ? updater(mine) : updater;
      // A header or center box is shared, so an edit on any sheet edits the one copy. Anything
      // else belongs to the page it was dropped on.
      const placed = b => {
        const sheet = isSharedZone(b.zone) ? SHARED_SHEET
          : (b.sheet == null || b.sheet === SHARED_SHEET) ? activeSheet : b.sheet;
        const dsZone = storedZoneFor(b, layout);
        return sheet === b.sheet && dsZone === b.dsZone ? b : { ...b, sheet, dsZone };
      };
      return [...others, ...next.map(placed)];
    });
  }, [setBoxes, activeSheet, layout]);

  const save = async (dropValues = false) => {
    const changes = layoutChanges(boxes, baselineRef.current);
    if (!changes.creates.length && !changes.updates.length && !changes.deletes.length) {
      setHasChanges(false);
      notify.success('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await dsProjectApi.saveLayout(row.dsConfig, { ...changes, dropValues }, auditCfg);
      setConfirm(null);
      savedRef.current = true;
      notify.success(`Saved ${data.dsIndex}: ${data.created} added, ${data.updated} changed, ${data.deleted} removed` +
        (data.valuesDropped ? `, ${data.valuesDropped} value${data.valuesDropped === 1 ? '' : 's'} deleted.` : '.'));
      await load();   // new boxes get their real ids and the baseline resets
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.requiresConfirm) {
        setConfirm({ kind: 'values', values: e.response.data.values ?? [] });
      } else {
        setConfirm(null);
        notify.error(errText(e, 'Save failed.'));
      }
    } finally { setSaving(false); }
  };

  const cancel = () => (hasChanges ? setConfirm({ kind: 'discard' }) : onClose(savedRef.current));

  const editable = !!meta?.editable;

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          <span className="stui-mono">{meta?.dsIndex ?? row.dsIndex}</span>
          <span className="stui-badge stui-badge-expired">Draft</span>
          {editable && sheetCount > 1 && (
            <div className="stui-module-header-tabs">
              {sheetNumbers.map(n => (
                <button key={n} className={`stui-dataentry-tab${activeSheet === n ? ' active' : ''}`}
                  onClick={() => setActiveSheet(n)} title={sheetLabel(n)}>
                  <span>{sheetLabel(n)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="stui-row-8">
          {editable && <span className="stui-text-sm-muted">Saved to the template library and this project.</span>}
          <button className="stui-btn stui-btn-secondary" onClick={cancel} disabled={saving}>
            {hasChanges ? 'Cancel' : 'Close'}
          </button>
          {editable && (
            <button className="stui-btn stui-btn-primary" onClick={() => save(false)} disabled={saving || !hasChanges}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        {loading ? <div className="stui-detail-empty">Loading…</div>
          : !editable ? <div className="stui-alert stui-alert-warning">{meta?.reason || 'This layout cannot be edited.'}</div>
          : <DSCanvas boxes={sheetBoxes} setBoxes={setSheetBoxes} sheetCount={sheetCount} layout={layout} allBoxes={boxes} />}
      </div>

      {confirm?.kind === 'values' && (
        <ConfirmModal title="Delete values with the removed boxes?" confirmLabel="Delete values and save" danger busy={saving}
          onConfirm={() => save(true)} onClose={() => setConfirm(null)}>
          <p className="stui-hint">
            {`${confirm.values.length} value${confirm.values.length === 1 ? '' : 's'} sit on boxes this save removes. ` +
              'They are deleted with the boxes (each deletion is audited). Cancel to keep the boxes and their values.'}
          </p>
          <table className="stui-data-grid stui-data-grid-auto">
            <thead><tr><th>Index Sheet</th><th>Page</th><th>Line</th><th>Box</th><th>Value</th></tr></thead>
            <tbody>
              {confirm.values.slice(0, SHOWN_VALUES).map(v => (
                <tr key={v.valueId}>
                  <td className="stui-mono">{v.indexSheet}</td>
                  <td>{v.sheet}</td>
                  <td>{v.line}</td>
                  <td>{v.label || v.boxType}</td>
                  <td>{v.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {confirm.values.length > SHOWN_VALUES && (
            <p className="stui-hint">{`…and ${confirm.values.length - SHOWN_VALUES} more.`}</p>
          )}
        </ConfirmModal>
      )}
      {confirm?.kind === 'discard' && (
        <ConfirmModal title="Discard unsaved changes?" confirmLabel="Discard" danger
          onConfirm={() => onClose(savedRef.current)} onClose={() => setConfirm(null)}>
          <p className="stui-hint">The boxes you changed have not been saved. Closing the editor throws those changes away.</p>
        </ConfirmModal>
      )}
    </>
  );
}
